import { FantazUIError, ProfileMatch } from '@fantaz/shared/data-access/models';
import { VerificationAttemptLimits } from './account-service.interface';
import { VERIFICATION_TYPES } from '@fantaz/shared/config/static';
import { VerifyUserDto } from './account-service.dto';

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
export class VerificationException extends FantazUIError {
  constructor(code: string) {
    super('Verification Error', code);
  }
}

export class VerificationEscalateException extends VerificationException {
  constructor() {
    super(VERIFICATION_TYPES.ESCALATE);
  }
}

export class VerificationDuplicateException extends VerificationException {
  override message =
    'Your information was already used to verify another account. Please contact customer support or logout to use your existing account.';

  constructor(profileMatch: ProfileMatch[]) {
    super(VERIFICATION_TYPES.DUPLICATE);
    if (profileMatch?.length) {
      const existingAccount = profileMatch[0];
      this.message = `Your information was already used to verify the following account: <b>${existingAccount?.email}</b>. If this is a mistake, please contact customer support or logout to use your existing account.`;
    }
  }
}

export class VerificationNoneException extends VerificationException {
  constructor() {
    super(VERIFICATION_TYPES.NONE);
  }
}

export class VerificationRejectedException extends VerificationException {
  requestParams: VerifyUserDto;

  constructor() {
    super(VERIFICATION_TYPES.REJECTED);
  }
}

export class VerificationIncompleteException extends VerificationException {
  attempts?: VerificationAttemptLimits;

  constructor(args?: { attempts?: VerificationAttemptLimits }) {
    super(VERIFICATION_TYPES.INCOMPLETE);
    this.attempts = args?.attempts;
  }
}

export class VerifyUserExceptions {
  /**
   * Returns a VerificationException with the given code
   */
  static readonly unknown = (str: string) => new VerificationException(str);

  /**
   * Returns a VerificationException with the given code
   */
  static readonly none = () => new VerificationNoneException();

  /**
   * Returns a VerificationException with the given code
   */
  static readonly rejected = () => new VerificationRejectedException();

  /**
   * Returns a VerificationException with the given code
   */
  static readonly incomplete = (args?: {
    attempts?: VerificationAttemptLimits;
  }) => new VerificationIncompleteException(args);

  /**
   * Returns a VerificationException with the given code
   */
  static readonly escalate = () => new VerificationEscalateException();

  /**
   * Returns a VerificationException with the given code
   */
  static readonly duplicated = (args: any) =>
    new VerificationDuplicateException(args);
}
